"use client";

import Image from "@/components/CdnImage";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ImageLightbox } from "@/components/ImageLightbox";

export type CarouselSlide = {
  src: string;
  alt: string;
  caption: string;
};

type Props = {
  slides: CarouselSlide[];
  sizes?: string;
  autoMs?: number;
  pauseMs?: number;
};

export function StyleCarousel({
  slides,
  sizes = "(min-width: 1024px) 58vw, 100vw",
  autoMs = 4500,
  pauseMs = 8000,
}: Props) {
  const t = useTranslations("lightbox");
  const scrollerRef = useRef<HTMLUListElement | null>(null);
  const figureRef = useRef<HTMLElement | null>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = useCallback((next: number, behavior: ScrollBehavior = "smooth") => {
    const el = scrollerRef.current;
    if (!el) return;
    const count = slides.length;
    const target = ((next % count) + count) % count;
    const child = el.children.item(target) as HTMLElement | null;
    if (!child) return;
    el.scrollTo({ left: child.offsetLeft, behavior });
  }, [slides.length]);

  const handleUserInteract = useCallback(() => {
    setPaused(true);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => setPaused(false), pauseMs);
  }, [pauseMs]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const w = el.clientWidth;
        if (w === 0) return;
        const i = Math.round(el.scrollLeft / w);
        setIndex(Math.max(0, Math.min(slides.length - 1, i)));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [slides.length]);

  useEffect(() => {
    const el = figureRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setInView(entry.isIntersecting && entry.intersectionRatio >= 0.6);
        }
      },
      { threshold: [0, 0.6, 1] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !inView || lightbox !== null || slides.length <= 1) return;
    const id = setInterval(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const w = el.clientWidth;
      if (w === 0) return;
      const current = Math.round(el.scrollLeft / w);
      const next = (current + 1) % slides.length;
      const child = el.children.item(next) as HTMLElement | null;
      if (child) el.scrollTo({ left: child.offsetLeft, behavior: "smooth" });
    }, autoMs);
    return () => clearInterval(id);
  }, [paused, inView, lightbox, slides.length, autoMs]);

  useEffect(() => () => {
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
  }, []);

  const current = slides[index] ?? slides[0];

  // Only show caption if it is not identical to the previous slide's caption
  const showCaption =
    !index || slides[index].caption !== slides[index - 1]?.caption;

  return (
    <figure
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      ref={figureRef}
    >
      <div className="relative overflow-hidden border border-rule bg-paper">
        <ul
          aria-label="Style examples"
          className="flex aspect-[4/3] w-full snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onPointerDown={handleUserInteract}
          onTouchStart={handleUserInteract}
          onWheel={handleUserInteract}
          ref={scrollerRef}
        >
          {slides.map((slide, i) => (
            <li
              aria-hidden={i !== index}
              className="relative h-full w-full shrink-0 snap-center"
              key={slide.src}
            >
              <Image
                alt={slide.alt}
                className="object-cover"
                fill
                priority={i === 0}
                sizes={sizes}
                src={slide.src}
              />
              {/* Tap anywhere on the slide to enlarge; a swipe still scrolls the snap track. */}
              <button
                aria-label={t("enlarge", { name: slide.alt })}
                className="absolute inset-0 cursor-zoom-in"
                onClick={() => {
                  handleUserInteract();
                  setLightbox(i);
                }}
                // Off-screen slides are aria-hidden, so their buttons must leave the tab order.
                tabIndex={i === index ? 0 : -1}
                type="button"
              />
            </li>
          ))}
        </ul>

        {slides.length > 1 ? (
          <>
            <button
              aria-label="Previous example"
              className="absolute left-2 top-1/2 -translate-y-1/2 border border-rule bg-paper/85 px-2 py-1 font-mono text-xs uppercase tracking-[0.08em] text-ink hover:bg-paper"
              onClick={() => { handleUserInteract(); goTo(index - 1); }}
              type="button"
            >
              ‹
            </button>
            <button
              aria-label="Next example"
              className="absolute right-2 top-1/2 -translate-y-1/2 border border-rule bg-paper/85 px-2 py-1 font-mono text-xs uppercase tracking-[0.08em] text-ink hover:bg-paper"
              onClick={() => { handleUserInteract(); goTo(index + 1); }}
              type="button"
            >
              ›
            </button>
          </>
        ) : null}
      </div>

      <div className="mt-3 flex items-center justify-between gap-4">
        {showCaption && (
          <figcaption className="font-mono text-xs uppercase tracking-[0.08em] text-ink-faint">
            {current?.caption}
          </figcaption>
        )}
        {slides.length > 1 ? (
          <div className="flex items-center gap-1.5">
            {slides.map((slide, i) => (
              <button
                aria-label={`Go to example ${i + 1}`}
                className={`h-1.5 w-4 border border-rule transition-colors ${
                  i === index ? "bg-ink" : "bg-transparent hover:bg-ink-faint/40"
                }`}
                key={slide.src}
                onClick={() => { handleUserInteract(); goTo(i); }}
                type="button"
              />
            ))}
          </div>
        ) : null}
      </div>

      {lightbox !== null ? (
        <ImageLightbox
          index={lightbox}
          labels={{ close: t("close"), previous: t("previous"), next: t("next") }}
          onClose={() => setLightbox(null)}
          onIndexChange={setLightbox}
          slides={slides.map((slide) => ({ src: slide.src, alt: slide.alt, caption: slide.caption }))}
        />
      ) : null}
    </figure>
  );
}
